import React, { useState, useEffect, useRef } from 'react';
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { getDefaultDomains, updateDefaultDomains } from "../services/ClientService";

import ConfirmModal from './ConfirmModal'; // adjust the path as needed
import { toast } from 'react-toastify';
import { adminFetchJson } from '../services/apiService';

const Domains = () => {
    const [domains, setDomains] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [domainInput, setDomainInput] = useState('https://');
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [domainToDelete, setDomainToDelete] = useState(null);
    const domainInputRef = useRef(null); // Create a ref for the input
    const [loading, setLoading] = useState(false);
    const [fixedDomain, setFixedDomain] = useState({ dev: 'https://localhost:8100', prod: 'https://www.zuntly.com' });
    const [isFixedModalOpen, setIsFixedModalOpen] = useState(false);

    useEffect(() => {
        fetchDomains();
        fetchDefaultDomains();
    }, []);

    useEffect(() => {
        if (isModalOpen) {
            domainInputRef.current.focus(); // Focus the input when modal opens
        }
    }, [isModalOpen]);

    const fetchDomains = async () => {
        setLoading(true);  // Start loading
        try {
            const { response, data } = await adminFetchJson('/services/getDomains', {
                method: 'POST',
                body: {},
            });

            if (!response.ok) throw new Error('Failed to fetch domains');
            setDomains(data.domains);
        } catch (error) {
            console.error('Error fetching domains:', error);
            toast.error('Failed to fetch domains');
        } finally {
            setLoading(false);  // Stop loading
        }
    };

    const fetchDefaultDomains = async () => {
        try {
            const data = await getDefaultDomains();
            console.log('Default Domains:', data);
            if (data.domains.length > 0) {
                let localDomain = data.domains.find(domain => domain.default && domain.type === 'dev');
                let productionDomain = data.domains.find(domain => domain.default && domain.type === 'prod');
                setFixedDomain({
                    dev: localDomain ? localDomain.domainName : '',
                    prod: productionDomain ? productionDomain.domainName : '',
                });
            } else {
                console.warn('No default domains found');
                setFixedDomain({ dev: '', prod: '' });
            }
        } catch (error) {
            console.error('Error fetching default domains:', error);
            toast.error('Failed to fetch default domains');
        }
    };

    const handleUpdateDefaultDomains = async () => {
        try {
            const data = await updateDefaultDomains(fixedDomain);
            console.log('Update Response:', data);
            toast.success('Fixed domains updated successfully!');
            setIsFixedModalOpen(false);
        } catch (error) {
            console.error('Error updating default domains:', error);
            toast.error('Failed to update fixed domains');
        }
    };

    const handleAddDomain = async () => {
        const domainName = domainInput.replace('https://', '').trim();
        if (!domainName) return;

        try {
            const { response } = await adminFetchJson('/services/createDomain', {
                method: 'POST',
                body: { domainName },
            });

            if (!response.ok) throw new Error('Failed to save domain');

            await fetchDomains();
            setIsModalOpen(false);
            setDomainInput('https://');
        } catch (error) {
            console.error('Error saving domain:', error);
            toast.error('Failed to save domain');
        }
    };

    const handleDeleteRequest = (domainId) => {
        setDomainToDelete(domainId);
        setConfirmModalVisible(true);
    };

    const confirmDeleteDomain = async () => {
        try {
            const { response } = await adminFetchJson('/services/deleteDomain', {
                method: 'POST',
                body: { _id: domainToDelete },
            });

            if (!response.ok) throw new Error('Failed to delete domain');

            await fetchDomains();
        } catch (error) {
            console.error('Error deleting domain:', error);
            toast.error('Failed to delete domain');
        } finally {
            setConfirmModalVisible(false);
            setDomainToDelete(null);
        }
    };

    return (
        <div className="h-screen overflow-hidden max-h-fit bg-white">
            <div className="bg-white p-6 sticky top-0 z-10 space-y-4">
                <div className="flex justify-between items-center">
                    <h1 className="text-3xl font-bold">Domains</h1>
                    <div className="flex gap-4">
                        <button
                            onClick={() => setIsFixedModalOpen(true)}
                            className="relative text-white font-bold bg-gradient-to-r from-green-500 to-teal-500 border-2 border-transparent rounded-full px-5 py-2.5 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-xl group"
                        >
                            <span className="absolute inset-0 rounded-full border-2 border-green-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"></span>
                            <span className="relative z-10 flex items-center">
                                <FiPlus className="mr-2 text-2xl" />
                                Fixed Domains
                            </span>
                        </button>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="relative text-white font-bold bg-gradient-to-r from-blue-500 to-purple-500 border-2 border-transparent rounded-full px-5 py-2.5 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-xl group"
                        >
                            <span className="absolute inset-0 rounded-full border-2 border-blue-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"></span>
                            <span className="relative z-10 flex items-center">
                                <FiPlus className="mr-2 text-2xl" />
                                Add Domain
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Domains List */}
            {loading ? (
                <div className="absolute inset-0 flex justify-center items-center bg-white/80 rounded-xl z-50">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-5 pt-3">
                    {domains.map((domain) => (
                        <div key={domain._id} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow flex justify-between items-center">
                            <span className="text-gray-700 font-medium">https://{domain.domainName}</span>
                            <button
                                onClick={() => handleDeleteRequest(domain._id)}
                                className="text-red-500 hover:text-red-600 p-1.5 rounded-full hover:bg-red-50 transition-colors"
                            >
                                <FiTrash2 className="w-5 h-5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Fixed Domains Section */}
            <div className="p-5 pt-3">
                <h2 className="text-2xl font-bold mb-4">Fixed Domains for Offline Stores</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg shadow-sm border border-green-200">
                        <h3 className="text-lg font-semibold text-green-800 mb-2">Development</h3>
                        <p className="text-gray-700">{fixedDomain.dev}/&#123;branch name&#125;</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-200">
                        <h3 className="text-lg font-semibold text-blue-800 mb-2">Production</h3>
                        <p className="text-gray-700">{fixedDomain.prod}/&#123;branch name&#125;</p>
                    </div>
                </div>
            </div>


            {/* Add Domain Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center">
                    <div className="bg-white p-6 rounded-lg shadow-2xl transform transition-transform duration-500 ease-in-out scale-100 mx-auto w-11/12 max-w-md sm:w-4/5 sm:mt-16 md:max-w-lg lg:max-w-xl">
                        <h2 className="text-xl sm:text-2xl font-bold mb-2 text-gray-800 text-center">Add New Domain</h2>

                        {/* Domain Input */}
                        <div className="mb-4">
                            <label className="block font-medium text-gray-700 mb-1">Domain</label>
                            <div className="flex items-center border-[1px] border-gray-400 rounded-md shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-0">
                                <span className="px-3 py-[7px] text-gray-500 border-r border-gray-400">https://</span>
                                <input
                                    type="text"
                                    value={domainInput.replace('https://', '')}
                                    onChange={(e) => setDomainInput(`https://${e.target.value}`)}
                                    placeholder="your-domain.com"
                                    ref={domainInputRef} // Attach the ref to the input
                                    className="flex-1 px-3 py-[7px] outline-none border-0 bg-transparent"
                                />
                            </div>
                        </div>

                        {/* Modal Actions */}
                        <div className="flex mt-6 gap-6">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="w-1/2 bg-gradient-to-r from-gray-500 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white px-6 py-3 rounded-lg shadow-lg transform transition-all hover:scale-105 focus:outline-none focus:ring-4 focus:ring-gray-400"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddDomain}
                                className="w-1/2 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white px-6 py-3 rounded-lg shadow-lg transform transition-all hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-400"
                            >
                                Save Domain
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Delete Modal */}
            <ConfirmModal
                visible={confirmModalVisible}
                message="Are you sure you want to delete this Domain?"
                onConfirm={confirmDeleteDomain}
                onCancel={() => {
                    setConfirmModalVisible(false);
                    setDomainToDelete(null);
                }}
                cancelButtonText="Cancel"
                confirmButtonText="Delete"
            />

            {/* Fixed Domain Modal */}
            {isFixedModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center">
                    <div className="bg-white p-6 rounded-lg shadow-2xl transform transition-transform duration-500 ease-in-out scale-100 mx-auto w-11/12 max-w-md sm:w-4/5 sm:mt-16 md:max-w-lg lg:max-w-xl">
                        <h2 className="text-xl sm:text-2xl font-bold mb-2 text-gray-800 text-center">Fixed Domains for Offline Stores</h2>

                        {/* Dev Domain Input */}
                        <div className="mb-4">
                            <label className="block font-medium text-gray-700 mb-1">Development Domain</label>
                            <input
                                type="text"
                                value={fixedDomain.dev}
                                onChange={(e) => setFixedDomain(prev => ({ ...prev, dev: e.target.value }))}
                                placeholder="https://localhost:3006"
                                className="w-full px-3 py-2 border border-gray-400 rounded-md shadow-sm focus:ring-2 focus:ring-green-500 focus:border-0"
                            />
                        </div>

                        {/* Prod Domain Input */}
                        <div className="mb-4">
                            <label className="block font-medium text-gray-700 mb-1">Production Domain</label>
                            <input
                                type="text"
                                value={fixedDomain.prod}
                                onChange={(e) => setFixedDomain(prev => ({ ...prev, prod: e.target.value }))}
                                placeholder="https://www.zuntly.com"
                                className="w-full px-3 py-2 border border-gray-400 rounded-md shadow-sm focus:ring-2 focus:ring-green-500 focus:border-0"
                            />
                        </div>

                        {/* Modal Actions */}
                        <div className="flex mt-6 gap-6">
                            <button
                                onClick={() => setIsFixedModalOpen(false)}
                                className="w-1/2 bg-gradient-to-r from-gray-500 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white px-6 py-3 rounded-lg shadow-lg transform transition-all hover:scale-105 focus:outline-none focus:ring-4 focus:ring-gray-400"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateDefaultDomains}
                                className="w-1/2 bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 text-white px-6 py-3 rounded-lg shadow-lg transform transition-all hover:scale-105 focus:outline-none focus:ring-4 focus:ring-green-400"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Domains;